import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, MapPin, CreditCard, ArrowRight, Key, Car } from 'lucide-react';
import { Elements, PaymentElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { useToast } from '../hooks/use-toast';
import BookingCalculator from '../components/BookingCalculator';

const STRIPE_CARD_FEE_RATE = 0;
const STRIPE_CARD_FEE_FIXED_CENTS = 0;

const formatAudFromCents = (valueInCents) => {
  const cents = Number(valueInCents);
  if (!Number.isFinite(cents)) return '$--';
  return `$${(cents / 100).toFixed(2)}`;
};

const getStripeChargeBreakdown = (servicePriceDollars) => {
  const price = Number(servicePriceDollars);
  if (!Number.isFinite(price) || price <= 0) return null;

  const serviceAmountCents = Math.round(price * 100);
  const processingFeeCents = Math.round(
    serviceAmountCents * STRIPE_CARD_FEE_RATE + STRIPE_CARD_FEE_FIXED_CENTS
  );
  const totalChargeCents = serviceAmountCents + processingFeeCents;

  return { serviceAmountCents, processingFeeCents, totalChargeCents };
};

const stripePublishableKey = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;
const stripeKeyMode = stripePublishableKey.startsWith('pk_live_')
  ? 'live'
  : stripePublishableKey.startsWith('pk_test_')
    ? 'test'
    : stripePublishableKey
      ? 'unknown'
      : 'missing';

const stripeAppearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#2563eb',
    colorBackground: '#ffffff',
    colorText: '#0f172a',
    colorDanger: '#ef4444',
    fontFamily: '"Manrope", "Segoe UI", Arial, sans-serif',
    borderRadius: '12px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      boxShadow: 'none',
    },
    '.Tab, .Block': {
      borderRadius: '12px',
    },
  },
};

const bookingQueryParamKeys = [
  'bedrooms',
  'bathrooms',
  'servicetype',
  'cleantype',
  'frequency',
  'hours',
  'minutes',
  'preferreddate',
  'preferredtime',
  'firstname',
  'lastname',
  'email',
  'phone',
  'streetaddress',
  'address',
  'suburb',
  'postcode',
  'specialinstructions',
  'carpetoptions',
  'carpetoption',
  'getinoption',
  'parkoption',
  'extrahours',
  'charge',
  'price',
  'serviceid',
];

const StripePaymentForm = ({
  clientSecret,
  amountCents,
  amountLabel,
  billingDetails,
  onPaymentSuccess,
  disabled,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [walletSupport, setWalletSupport] = useState(null);
  const onPaymentSuccessRef = useRef(onPaymentSuccess);

  useEffect(() => {
    onPaymentSuccessRef.current = onPaymentSuccess;
  }, [onPaymentSuccess]);

  useEffect(() => {
    if (!stripe || !clientSecret || disabled) {
      setPaymentRequest(null);
      setWalletSupport(null);
      return;
    }

    const parsedAmount = Number(amountCents);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setPaymentRequest(null);
      setWalletSupport(null);
      return;
    }

    const pr = stripe.paymentRequest({
      country: 'AU',
      currency: 'aud',
      total: {
        label: 'Booking total',
        amount: Math.round(parsedAmount),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    let isCancelled = false;

    const handlePaymentMethod = async (event) => {
      if (!stripe) return;

      setIsProcessing(true);
      setErrorMessage('');

      try {
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: event.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          event.complete('fail');
          setErrorMessage(confirmError.message || 'Payment failed. Please try again.');
          return;
        }

        event.complete('success');

        let finalIntent = paymentIntent;
        if (finalIntent?.status === 'requires_action') {
          const { error: actionError, paymentIntent: actionIntent } = await stripe.confirmCardPayment(clientSecret);
          if (actionError) {
            setErrorMessage(actionError.message || 'Payment authentication failed. Please try again.');
            return;
          }
          finalIntent = actionIntent;
        }

        if (finalIntent?.status === 'succeeded') {
          await onPaymentSuccessRef.current?.(finalIntent);
          return;
        }

        setErrorMessage('Payment was not completed. Please try again.');
      } catch (error) {
        try {
          event.complete('fail');
        } catch (_) {
          // no-op
        }
        setErrorMessage(error?.message || 'Payment failed. Please try again.');
      } finally {
        setIsProcessing(false);
      }
    };

    pr.on('paymentmethod', handlePaymentMethod);

    pr.canMakePayment()
      .then((result) => {
        if (isCancelled) return;
        if (result) {
          setPaymentRequest(pr);
          setWalletSupport(result);
        } else {
          setPaymentRequest(null);
          setWalletSupport(null);
        }
      })
      .catch(() => {
        if (isCancelled) return;
        setPaymentRequest(null);
        setWalletSupport(null);
      });

    return () => {
      isCancelled = true;
      if (typeof pr?.off === 'function') {
        pr.off('paymentmethod', handlePaymentMethod);
      }
    };
  }, [stripe, clientSecret, amountCents, disabled]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || !clientSecret || disabled) return;

    setIsProcessing(true);
    setErrorMessage('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        payment_method_data: {
          billing_details: billingDetails,
        },
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      await onPaymentSuccess(paymentIntent);
      setIsProcessing(false);
      return;
    }

    setErrorMessage('Payment was not completed. Please try again.');
    setIsProcessing(false);
  };

  const isDisabled = disabled || isProcessing || !stripe || !elements || !clientSecret;
  const walletLabel = walletSupport?.applePay && walletSupport?.googlePay
    ? 'Apple Pay or Google Pay'
    : walletSupport?.applePay
      ? 'Apple Pay'
      : walletSupport?.googlePay
        ? 'Google Pay'
        : '';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Payment Details</Label>
        <div className="mt-2 rounded-xl border border-slate-200 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] ring-1 ring-blue-500/10 transition focus-within:border-blue-500 focus-within:ring-blue-500/30">
          <div className="mb-3 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-slate-500">
            <span>Stripe</span>
            <span>Secure checkout</span>
          </div>
          {paymentRequest && (
            <div className="mb-4 space-y-3">
              <div className="text-xs font-medium text-slate-600">
                {walletLabel ? `Pay with ${walletLabel}` : 'Express checkout'}
              </div>
              <div className={isDisabled ? 'pointer-events-none opacity-60' : ''}>
                <PaymentRequestButtonElement
                  options={{
                    paymentRequest,
                    style: {
                      paymentRequestButton: {
                        type: 'buy',
                        theme: 'dark',
                        height: '44px',
                      },
                    },
                  }}
                />
              </div>
              <div className="relative flex items-center">
                <div className="flex-grow border-t border-slate-200" />
                <span className="mx-3 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                  Or pay with card
                </span>
                <div className="flex-grow border-t border-slate-200" />
              </div>
            </div>
          )}
          <div className="rounded-lg border border-slate-100 bg-gradient-to-br from-slate-50 to-white px-4 py-3">
            <PaymentElement options={{ layout: 'tabs' }} />
          </div>
        </div>
      </div>

      {errorMessage && (
        <p className="text-sm text-red-600">{errorMessage}</p>
      )}

      <Button
        type="submit"
        className="w-full bg-blue-600 hover:bg-blue-700"
        size="lg"
        disabled={isDisabled}
      >
        {isProcessing ? 'Processing...' : `Pay ${amountLabel} & Confirm Booking`}
      </Button>

      <p className="text-xs text-slate-500 text-center">
        Payments are processed securely by Stripe.
      </p>
    </form>
  );
};

const Book = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [date, setDate] = useState();
  const [bookingDetails, setBookingDetails] = useState({
    bookingType: 'size',
    bedrooms: '1',
    bathrooms: '2',
    hours: '3',
    minutes: '0',
    cleanType: 'standard',
    carpetOption: null,
    extraHours: 0,
    serviceType: 'standard',
    frequency: 'once',
    price: null,
  });
  const [requestId, setRequestId] = useState(() => localStorage.getItem('bookingRequestId'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState('');
  const [stripeIntentAmount, setStripeIntentAmount] = useState(null);
  const [stripeError, setStripeError] = useState('');
  const [stripeInitError, setStripeInitError] = useState('');
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    suburb: '',
    postcode: '',
    time: '',
    getInOption: '',
    getInOptionOther: '',
    parkOption: '',
    parkOptionOther: '',
    specialInstructions: ''
  });

  const getInOptions = [
    'I will be home',
    'I will leave a key',
    'I can provide lockbox access',
    'I can provide access code',
    'Other',
  ];

  const parkOptions = [
    'Park in the driveway',
    'Street parking',
    'Paid parking is available',
    'Other',
  ];

  const normalizeOptionValue = (value, options) => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (!trimmed) return { selected: '', other: '' };
    if (options.includes(trimmed)) return { selected: trimmed, other: '' };
    if (trimmed.toLowerCase() === 'other') return { selected: 'Other', other: '' };
    return { selected: 'Other', other: trimmed };
  };

  const resolveOptionValue = (selected, other) => {
    const selection = typeof selected === 'string' ? selected.trim() : '';
    if (!selection) return null;
    if (selection !== 'Other') return selection;
    const otherValue = typeof other === 'string' ? other.trim() : '';
    return otherValue || 'Other';
  };

  const backendBaseUrl = (() => {
    const fromEnv = (process.env.REACT_APP_BACKEND_URL || '').trim();
    const useLocalBackend = String(process.env.REACT_APP_USE_LOCAL_BACKEND || '').trim().toLowerCase() === 'true';
    const isBrowser = typeof window !== 'undefined' && window.location;
    const hostname = isBrowser ? window.location.hostname : '';
    const isLocalFrontend = hostname === 'localhost' || hostname === '127.0.0.1';

    const normalize = (value) => value.replace(/\/+$/, '');

    if (fromEnv) {
      const normalized = normalize(fromEnv);
      const isLocalBackend = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(normalized);
      if (isLocalBackend && (!isLocalFrontend || !useLocalBackend)) {
        return 'https://server.royalshine.com.au';
      }
      return normalized;
    }

    return 'https://server.royalshine.com.au';
  })();

  useEffect(() => {
    const params = new URLSearchParams(location?.search || '');
    const lowerKeys = new Set(Array.from(params.keys()).map((key) => key.toLowerCase()));
    const hasPrefillParams = bookingQueryParamKeys.some((key) => lowerKeys.has(key));
    if (!hasPrefillParams) return;

    const getParam = (key) => params.get(key) ?? params.get(key.toLowerCase());
    const rawHours = getParam('hours');
    const rawMinutes = getParam('minutes');
    const bookingTypeFromParams = rawHours || rawMinutes ? 'hourly' : 'size';

    const rawCharge = getParam('charge') ?? getParam('price');
    const parsedCharge = rawCharge !== null && rawCharge !== '' ? Number(rawCharge) : NaN;
    const rawExtraHours = getParam('extraHours') ?? getParam('extrahours');
    const parsedExtraHours = rawExtraHours !== null && rawExtraHours !== '' ? Number(rawExtraHours) : NaN;

    const preferredDateStr = getParam('preferredDate') || getParam('preferreddate');
    const preferredDateValue = (() => {
      if (!preferredDateStr) return null;
      const parsed = new Date(preferredDateStr);
      if (Number.isNaN(parsed.getTime())) return null;
      return parsed;
    })();

    const preferredTimeStr = getParam('preferredTime') || getParam('preferredtime');
    const normalizedTime = (() => {
      if (!preferredTimeStr) return '';
      const trimmed = preferredTimeStr.trim();
      if (!trimmed) return '';
      const parts = trimmed.split(':');
      if (parts.length >= 2) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
      }
      return trimmed;
    })();

    const rawServiceType = getParam('serviceType') || getParam('servicetype');
    const rawCleanType = getParam('cleanType') || getParam('cleantype') || rawServiceType;
    const rawFrequency = getParam('frequency');
    const rawCarpet = getParam('carpetOptions') || getParam('carpetOption') || getParam('carpetoptions');
    const rawBedrooms = getParam('bedrooms');
    const rawBathrooms = getParam('bathrooms');

    setBookingDetails((prev) => {
      const next = {
        ...prev,
        bookingType: bookingTypeFromParams,
        bedrooms: bookingTypeFromParams === 'size' ? (rawBedrooms || prev.bedrooms) : null,
        bathrooms: bookingTypeFromParams === 'size'
          ? (rawBathrooms || prev.bathrooms || rawBedrooms || prev.bedrooms)
          : prev.bathrooms,
        hours: bookingTypeFromParams === 'hourly' ? (rawHours || prev.hours || '0') : null,
        minutes: bookingTypeFromParams === 'hourly' ? (rawMinutes || prev.minutes || '0') : null,
        cleanType: rawCleanType || prev.cleanType,
        carpetOption: rawCarpet || prev.carpetOption,
        extraHours: Number.isFinite(parsedExtraHours) ? parsedExtraHours : prev.extraHours,
        serviceType: rawServiceType || rawCleanType || prev.serviceType,
        frequency: rawFrequency || prev.frequency,
        price: Number.isFinite(parsedCharge) ? parsedCharge : prev.price,
      };
      localStorage.setItem('bookingData', JSON.stringify(next));
      return next;
    });

    setFormData((prev) => ({
      ...prev,
      firstName: getParam('firstName') || getParam('firstname') || prev.firstName,
      lastName: getParam('lastName') || getParam('lastname') || prev.lastName,
      email: getParam('email') || prev.email,
      phone: getParam('phone') || prev.phone,
      address: getParam('streetAddress') || getParam('address') || prev.address,
      suburb: getParam('suburb') || prev.suburb,
      postcode: getParam('postcode') || prev.postcode,
      time: normalizedTime || prev.time,
      ...(() => {
        const getIn = normalizeOptionValue(getParam('getInOption') || getParam('getinoption'), getInOptions);
        const park = normalizeOptionValue(getParam('parkOption') || getParam('parkoption'), parkOptions);
        return {
          getInOption: getIn.selected || prev.getInOption,
          getInOptionOther: getIn.other || prev.getInOptionOther,
          parkOption: park.selected || prev.parkOption,
          parkOptionOther: park.other || prev.parkOptionOther,
        };
      })(),
      specialInstructions: getParam('specialInstructions') || getParam('specialinstructions') || prev.specialInstructions,
    }));

    if (preferredDateValue) {
      setDate(preferredDateValue);
    }

    const serviceIdFromParams = getParam('serviceId') || getParam('serviceid');
    if (serviceIdFromParams) {
      setRequestId(serviceIdFromParams);
      localStorage.setItem('bookingRequestId', serviceIdFromParams);
    }

    setStep(2);
  }, [location.search]);

  useEffect(() => {
    if (step !== 3) {
      setStripeInitError('');
      return;
    }

    if (!stripePromise) return;

    let isCancelled = false;
    (async () => {
      try {
        const stripe = await stripePromise;
        if (isCancelled) return;
        if (!stripe) {
          setStripeInitError(
            'Stripe failed to initialize. Ensure you are using HTTPS for live payments and that your publishable key matches your backend secret key mode (test/live).'
          );
        } else {
          setStripeInitError('');
        }
      } catch (error) {
        if (isCancelled) return;
        setStripeInitError(error?.message || 'Stripe failed to initialize.');
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [step]);

  useEffect(() => {
    // Load booking data from calculator
    const params = new URLSearchParams(location?.search || '');
    const lowerKeys = new Set(Array.from(params.keys()).map((key) => key.toLowerCase()));
    const hasPrefillParams = bookingQueryParamKeys.some((key) => lowerKeys.has(key));
    if (hasPrefillParams) return;

    const savedData = localStorage.getItem('bookingData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setBookingDetails((prev) => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error('Failed to parse saved bookingData', e);
      }
    }
  }, [location.search]);

  useEffect(() => {
    if (step !== 3) {
      setIsPaymentComplete(false);
      setStripeClientSecret('');
      setStripeIntentAmount(null);
      setStripeError('');
      setIsCreatingIntent(false);
      return;
    }

    if (!stripePromise) {
      setStripeError('Stripe publishable key is missing.');
      return;
    }

    const breakdown = getStripeChargeBreakdown(bookingDetails?.price);
    if (!breakdown) {
      setStripeError('Unable to determine the payment amount.');
      return;
    }

    const amount = breakdown.totalChargeCents;
    if (stripeIntentAmount === amount && stripeClientSecret) {
      return;
    }

    setIsPaymentComplete(false);

    let isCancelled = false;
    const createPaymentIntent = async () => {
      setIsCreatingIntent(true);
      setStripeError('');

      try {
        const response = await axios.post(
          `${backendBaseUrl}/api/stripe/create-payment-intent`,
          {
            amount,
            currency: 'aud',
            metadata: {
              serviceId: requestId || undefined,
              email: formData.email || undefined,
              serviceAmountCents: String(breakdown.serviceAmountCents),
              processingFeeCents: String(breakdown.processingFeeCents),
              totalChargeCents: String(breakdown.totalChargeCents),
            },
          }
        );

        if (isCancelled) return;

        setStripeClientSecret(response?.data?.clientSecret || '');
        setStripeIntentAmount(amount);
      } catch (err) {
        if (isCancelled) return;
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Unable to prepare your payment. Please try again.';
      setStripeError(message);
      toast({
        title: 'Payment setup failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      if (!isCancelled) setIsCreatingIntent(false);
    }
  };

  createPaymentIntent();

    return () => {
      isCancelled = true;
    };
  }, [
    step,
    bookingDetails?.price,
    stripeIntentAmount,
    stripeClientSecret,
    backendBaseUrl,
    requestId,
    formData.email,
    toast,
  ]);

  const persistStep = async (nextStep, extraPayload = {}) => {
    const details = extraPayload.bookingDetailsOverride || bookingDetails;
    if (!details) return { ok: false };
    if (nextStep < 2) return { ok: false };

    const buildPayload = (serviceIdValue) => {
      const payload = {
        serviceId: serviceIdValue ? Number(serviceIdValue) : undefined,
        step: nextStep,
        bookingType: details?.bookingType,
        bedrooms: details?.bookingType === 'size' ? details?.bedrooms : null,
        bathrooms: details?.bookingType === 'size' ? details?.bathrooms : null,
        serviceType: details?.serviceType,
        cleanType: details?.cleanType,
        carpetOption: details?.carpetOption,
        extraHours: details?.extraHours,
        frequency: details?.frequency,
        hours: details?.bookingType === 'hourly' ? details?.hours : null,
        minutes: details?.bookingType === 'hourly' ? details?.minutes : null,
        preferredDate: date ? format(date, 'yyyy-MM-dd') : null,
        preferredTime: formData.time || null,
        firstName: formData.firstName || null,
        lastName: formData.lastName || null,
        email: formData.email || null,
        phone: formData.phone || null,
        streetAddress: formData.address || null,
        suburb: formData.suburb || null,
        postcode: formData.postcode || null,
        getInOption: resolveOptionValue(formData.getInOption, formData.getInOptionOther),
        parkOption: resolveOptionValue(formData.parkOption, formData.parkOptionOther),
        specialInstructions: formData.specialInstructions || null,
        price: details?.price || null,
        charge: (() => {
          const breakdown = getStripeChargeBreakdown(details?.price);
          if (!breakdown) return details?.price || null;
          return Number((breakdown.totalChargeCents / 100).toFixed(2));
        })(),
        ...extraPayload,
      };
      if (typeof payload.finalize === 'undefined') {
        payload.finalize = false;
      }
      return payload;
    };

    setIsSavingProgress(true);
    try {
      let currentRequestId = requestId || localStorage.getItem('bookingRequestId');

      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const response = await axios.post(
            `${backendBaseUrl}/api/cleaning-service-requests`,
            buildPayload(currentRequestId)
          );

          const newId = response?.data?.data?.serviceId;
          const resolvedId = newId ? newId.toString() : currentRequestId;

          if (resolvedId) {
            setRequestId(resolvedId);
            localStorage.setItem('bookingRequestId', resolvedId);
          }

          return { ok: true, serviceId: resolvedId };
        } catch (err) {
          const status = err?.response?.status;

          if (status === 404 && currentRequestId && attempt === 0) {
            setRequestId(null);
            localStorage.removeItem('bookingRequestId');
            currentRequestId = null;
            continue;
          }

          throw err;
        }
      }

      return { ok: false };
    } catch (err) {
      console.error('Failed to persist booking progress', err);
      toast({
        title: 'Save failed',
        description: err?.response?.data?.message || err?.message || 'Unable to save your progress. Please try again.',
        variant: 'destructive',
      });
      return { ok: false };
    } finally {
      setIsSavingProgress(false);
    }
  };

  const handleBookingDataChange = (data) => {
    setBookingDetails(data);
    localStorage.setItem('bookingData', JSON.stringify(data));
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleNextStep = async () => {
    if (!Number.isFinite(bookingDetails?.price) || bookingDetails.price <= 0) {
      toast({
        title: 'Quote required',
        description: 'Please contact us for a quote for this property size.',
        variant: 'destructive'
      });
      return;
    }

    if (step === 1) {
      // Validate booking details
      if (!bookingDetails) {
        toast({
          title: 'No booking details',
          description: 'Please configure your booking details first.',
          variant: 'destructive'
        });
        return;
      }
      setStep(2);
    } else if (step === 2) {
      // Validate form
      if (!date || !formData.time) {
        toast({
          title: 'Missing information',
          description: 'Please select a date and time.',
          variant: 'destructive'
        });
        return;
      }
      if (!formData.firstName || !formData.email || !formData.phone || !formData.address) {
        toast({
          title: 'Missing information',
          description: 'Please fill in all required fields.',
          variant: 'destructive'
        });
        return;
      }
      const result = await persistStep(2);
      if (result?.ok) setStep(3);
    }
  };

  const handleSubmitBooking = async (paymentDetails = {}) => {
    if (!bookingDetails) {
      toast({
        title: 'Missing booking details',
        description: 'Please configure your booking before confirming.',
        variant: 'destructive'
      });
      return;
    }

    let currentRequestId = requestId || localStorage.getItem('bookingRequestId');
    if (!currentRequestId) {
      const saved = await persistStep(2);
      if (!saved?.ok) return;
      currentRequestId = saved?.serviceId || localStorage.getItem('bookingRequestId');
    }

    const payload = {
      serviceId: currentRequestId ? Number(currentRequestId) : undefined,
      step: 3,
      finalize: true,
      bookingType: bookingDetails.bookingType,
      bedrooms: bookingDetails.bookingType === 'size' ? bookingDetails.bedrooms : null,
      bathrooms: bookingDetails.bookingType === 'size' ? bookingDetails.bathrooms : null,
      serviceType: bookingDetails.serviceType,
      cleanType: bookingDetails.cleanType,
      carpetOption: bookingDetails.carpetOption,
      extraHours: bookingDetails.extraHours,
      frequency: bookingDetails.frequency,
      hours: bookingDetails.bookingType === 'hourly' ? bookingDetails.hours : null,
      minutes: bookingDetails.bookingType === 'hourly' ? bookingDetails.minutes : null,
      preferredDate: date ? format(date, 'yyyy-MM-dd') : null,
      preferredTime: formData.time,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      streetAddress: formData.address,
      suburb: formData.suburb,
      postcode: formData.postcode,
      getInOption: resolveOptionValue(formData.getInOption, formData.getInOptionOther),
      parkOption: resolveOptionValue(formData.parkOption, formData.parkOptionOther),
      specialInstructions: formData.specialInstructions,
      price: bookingDetails.price,
      charge: (() => {
        const breakdown = getStripeChargeBreakdown(bookingDetails.price);
        if (!breakdown) return bookingDetails.price;
        return Number((breakdown.totalChargeCents / 100).toFixed(2));
      })(),
      paymentIntentId: paymentDetails?.id,
      paymentStatus: paymentDetails?.status,
      paymentAmount: paymentDetails?.amount,
      paymentCurrency: paymentDetails?.currency,
      paymentMethodTypes: paymentDetails?.payment_method_types,
    };

    try {
      setIsSubmitting(true);
      try {
        await axios.post(`${backendBaseUrl}/api/cleaning-service-requests`, payload);
      } catch (error) {
        if (error?.response?.status === 404 && payload.serviceId) {
          setRequestId(null);
          localStorage.removeItem('bookingRequestId');

          const saved = await persistStep(2);
          if (!saved?.ok) throw error;

          const recoveredId = saved?.serviceId || localStorage.getItem('bookingRequestId');
          if (!recoveredId) throw error;

          await axios.post(`${backendBaseUrl}/api/cleaning-service-requests`, {
            ...payload,
            serviceId: Number(recoveredId),
          });
        } else {
          throw error;
        }
      }

      localStorage.removeItem('bookingData');
      localStorage.removeItem('bookingRequestId');
      navigate('/booking-confirmation', { replace: true });
    } catch (error) {
      console.error('Failed to submit booking', error);
      toast({
        title: 'Booking failed',
        description: error?.response?.data?.message || error?.message || 'Unable to submit your booking. Please try again.',
        variant: 'destructive'
      });
      alert('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stripeChargeBreakdown = getStripeChargeBreakdown(bookingDetails?.price);
  const amountLabel = stripeChargeBreakdown
    ? formatAudFromCents(stripeChargeBreakdown.totalChargeCents)
    : '$--';
  const serviceAmountLabel = stripeChargeBreakdown
    ? formatAudFromCents(stripeChargeBreakdown.serviceAmountCents)
    : '$--';
  const processingFeeLabel = stripeChargeBreakdown
    ? formatAudFromCents(stripeChargeBreakdown.processingFeeCents)
    : '$--';
  const billingDetails = {
    name: [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim(),
    email: formData.email || undefined,
    phone: formData.phone || undefined,
    address: formData.address || formData.suburb || formData.postcode
      ? {
          line1: formData.address || undefined,
          city: formData.suburb || undefined,
          postal_code: formData.postcode || undefined,
          country: 'AU',
        }
      : undefined,
  };
  const paymentDisabled = isSubmitting || isSavingProgress || isCreatingIntent || isPaymentComplete;

  return (
    <div className="min-h-screen pt-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-full font-bold ${
                    step >= s
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div
                    className={`h-1 w-16 ${
                      step > s ? 'bg-blue-600' : 'bg-slate-200'
                    }`}
                  ></div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-center space-x-20 mt-4">
            <span className={step >= 1 ? 'text-blue-600 font-semibold' : 'text-slate-500'}>
              Configure
            </span>
            <span className={step >= 2 ? 'text-blue-600 font-semibold' : 'text-slate-500'}>
              Details
            </span>
            <span className={step >= 3 ? 'text-blue-600 font-semibold' : 'text-slate-500'}>
              Payment
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {step === 1 && (
              <Card className="p-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Configure Your Booking</h2>
                <BookingCalculator onBookingDataChange={handleBookingDataChange} showbtn = {false}/>
                <div className="mt-6">
                  <Button
                    onClick={handleNextStep}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    size="lg"
                  >
                    Continue to Details
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </Card>
            )}

            {step === 2 && (
              <Card className="p-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Booking Details</h2>
                <div className="space-y-6">
                  {/* Date Selection */}
                  <div>
                    <Label>Preferred Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal mt-2"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Time Selection */}
                  <div>
                    <Label htmlFor="time">Preferred Time *</Label>
                    <Input
                      id="time"
                      name="time"
                      type="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      className="mt-2"
                    />
                  </div>

                  {/* Personal Details */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="mt-2"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="suburb">Suburb *</Label>
                      <Input
                        id="suburb"
                        name="suburb"
                        value={formData.suburb}
                        onChange={handleInputChange}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postcode">Postcode *</Label>
                      <Input
                        id="postcode"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleInputChange}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Access + Parking */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="getInOption" className="flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        How can we get in
                      </Label>
                      <Select
                        value={formData.getInOption || undefined}
                        onValueChange={(value) => {
                          setFormData((prev) => ({
                            ...prev,
                            getInOption: value,
                            getInOptionOther: value === 'Other' ? prev.getInOptionOther : '',
                          }));
                        }}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="How can we get in" />
                        </SelectTrigger>
                        <SelectContent>
                          {getInOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.getInOption === 'Other' && (
                        <Input
                          id="getInOptionOther"
                          name="getInOptionOther"
                          value={formData.getInOptionOther}
                          onChange={handleInputChange}
                          className="mt-2"
                          placeholder="Please specify..."
                        />
                      )}
                    </div>

                    <div>
                      <Label htmlFor="parkOption" className="flex items-center gap-2">
                        <Car className="w-4 h-4" />
                        Where can we park
                      </Label>
                      <Select
                        value={formData.parkOption || undefined}
                        onValueChange={(value) => {
                          setFormData((prev) => ({
                            ...prev,
                            parkOption: value,
                            parkOptionOther: value === 'Other' ? prev.parkOptionOther : '',
                          }));
                        }}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Where can we park" />
                        </SelectTrigger>
                        <SelectContent>
                          {parkOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.parkOption === 'Other' && (
                        <Input
                          id="parkOptionOther"
                          name="parkOptionOther"
                          value={formData.parkOptionOther}
                          onChange={handleInputChange}
                          className="mt-2"
                          placeholder="Please specify..."
                        />
                      )}
                    </div>
                  </div>

                  {/* Special Instructions */}
                  <div>
                    <Label htmlFor="specialInstructions">Special Instructions</Label>
                    <Textarea
                      id="specialInstructions"
                      name="specialInstructions"
                      value={formData.specialInstructions}
                      onChange={handleInputChange}
                      className="mt-2"
                      rows={4}
                      placeholder="Any special requirements or access instructions..."
                    />
                  </div>

                  <div className="flex space-x-4">
                    <Button
                      onClick={() => {
                        setStep(1);
                      }}
                      variant="outline"
                      size="lg"
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleNextStep}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      size="lg"
                      disabled={isSavingProgress}
                    >
                      {isSavingProgress ? 'Saving...' : 'Continue to Payment'}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {step === 3 && (
              <Card className="p-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Payment</h2>
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="text-center text-slate-700">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 text-blue-600" />
                      <p className="font-medium">Pay securely to confirm your booking.</p>
                    </div>

                    {stripeChargeBreakdown && (
                      <div className="mt-4 rounded-lg bg-white/70 p-4 text-sm text-slate-700">
                        <div className="flex items-center justify-between">
                          <span>Service amount</span>
                          <span className="font-medium">{serviceAmountLabel}</span>
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <span>Card processing fee </span>
                          <span className="font-medium">{processingFeeLabel}</span>
                        </div>
                        <div className="mt-3 flex items-center justify-between border-t border-blue-200/70 pt-3 text-base font-semibold text-slate-900">
                          <span>Total charged</span>
                          <span>{amountLabel} AUD</span>
                        </div>
                        <p className="mt-2 text-xs text-slate-600">
                          This fee covers Stripe card processing costs.
                        </p>
                      </div>
                    )}
                  </div>

                  {!stripePromise && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                      Stripe publishable key is missing. Add `REACT_APP_STRIPE_PUBLISHABLE_KEY` to `frontend/.env` to enable payments.
                    </div>
                  )}

                  {stripePromise && (
                    <>
                      {stripeKeyMode === 'live' && typeof window !== 'undefined' && window.location.protocol !== 'https:' && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                          Live Stripe payments require HTTPS. Use an `https://` URL (or switch to test keys for local `http://` development).
                        </div>
                      )}

                      {stripeInitError && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                          {stripeInitError}
                        </div>
                      )}

                      {isCreatingIntent && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                          Preparing your payment details...
                        </div>
                      )}

                      {stripeError && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                          {stripeError}
                        </div>
                      )}

                      {stripeClientSecret && (
                        <Elements
                          stripe={stripePromise}
                          options={{ clientSecret: stripeClientSecret, appearance: stripeAppearance }}
                        >
                          <StripePaymentForm
                            clientSecret={stripeClientSecret}
                            amountCents={stripeIntentAmount}
                            amountLabel={amountLabel}
                            billingDetails={billingDetails}
                            disabled={paymentDisabled}
                            onPaymentSuccess={async (paymentIntent) => {
                              setIsPaymentComplete(true);
                              await handleSubmitBooking(paymentIntent);
                            }}
                          />
                        </Elements>
                      )}

                      {!isCreatingIntent && !stripeClientSecret && !stripeError && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                          Payment form will appear once we prepare your payment.
                        </div>
                      )}
                    </>
                  )}

                  <div className="flex space-x-4">
                    <Button
                      onClick={() => {
                        setStep(2);
                        persistStep(2);
                      }}
                      variant="outline"
                      size="lg"
                      className="flex-1"
                      disabled={isSubmitting || isCreatingIntent}
                    >
                      Back
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Summary Sidebar */}
          <div>
            <Card className="p-6 sticky top-24">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Booking Summary</h3>
              {bookingDetails ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-600">Service Type</p>
                    <p className="font-semibold text-slate-900 capitalize">
                      {bookingDetails.cleanType} Clean
                    </p>
                  </div>
                  {bookingDetails.bookingType === 'size' ? (
                    <>
                      <div>
                        <p className="text-sm text-slate-600">Property Size</p>
                        <p className="font-semibold text-slate-900">
                          {bookingDetails.bedrooms} Bedroom, {bookingDetails.bathrooms} Bathroom
                        </p>
                      </div>
                    </>
                  ) : (
                    <div>
                      <p className="text-sm text-slate-600">Duration</p>
                      <p className="font-semibold text-slate-900">
                        {bookingDetails.hours} hours {bookingDetails.minutes} minutes
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-600">Frequency</p>
                    <p className="font-semibold text-slate-900 capitalize">
                      {bookingDetails.frequency === 'once' ? 'Once Off' : bookingDetails.frequency}
                    </p>
                  </div>
                  {date && (
                    <div>
                      <p className="text-sm text-slate-600">Date</p>
                      <p className="font-semibold text-slate-900">{format(date, 'PPP')}</p>
                    </div>
                  )}
                  {formData.time && (
                    <div>
                      <p className="text-sm text-slate-600">Time</p>
                      <p className="font-semibold text-slate-900">{formData.time}</p>
                    </div>
                  )}
                  {step !== 3 && (
                    <div className="pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-600 mb-2">Total Price</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {bookingDetails.price === null ? 'Contact us for a quote' : `$${bookingDetails.price ?? '--'}`}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-600">Configure your booking to see the summary</p>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Book;
